import * as React from 'react';
import { cn } from '@/lib/utils';

interface DateInputProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  minYear?: number;
  maxYear?: number;
  placeholder?: { day?: string; month?: string; year?: string };
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

/**
 * DateInput - 3 camps numèrics amb auto-focus
 * Format: DD / MM / AAAA (o AA per any curt)
 * Quan s'omplen els dígits, salta automàticament al següent camp
 */
export function DateInput({
  value,
  onChange,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  placeholder = { day: 'DD', month: 'MM', year: 'AAAA' },
  disabled = false,
  className,
  error = false,
}: DateInputProps) {
  const dayRef = React.useRef<HTMLInputElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const yearRef = React.useRef<HTMLInputElement>(null);
  const isUserTyping = React.useRef(false);

  // Parse initial value
  const [day, setDay] = React.useState(() => value ? String(value.getDate()).padStart(2, '0') : '');
  const [month, setMonth] = React.useState(() => value ? String(value.getMonth() + 1).padStart(2, '0') : '');
  const [year, setYear] = React.useState(() => value ? String(value.getFullYear()) : '');

  // Sync with external value changes (only if not typing)
  React.useEffect(() => {
    if (isUserTyping.current) return;
    
    if (value) {
      setDay(String(value.getDate()).padStart(2, '0'));
      setMonth(String(value.getMonth() + 1).padStart(2, '0'));
      setYear(String(value.getFullYear()));
    } else if (value === null) {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [value]);

  // Validate and emit date
  const emitDate = React.useCallback((d: string, m: string, y: string) => {
    const dayNum = parseInt(d, 10);
    const monthNum = parseInt(m, 10);
    const yearNum = parseInt(y, 10);

    // Need all fields
    if (!d || !m || !y || y.length < 4) {
      return;
    }

    // Basic validation
    if (dayNum < 1 || dayNum > 31) return;
    if (monthNum < 1 || monthNum > 12) return;
    if (yearNum < minYear || yearNum > maxYear) return;

    // Create date and validate
    const date = new Date(yearNum, monthNum - 1, dayNum);
    
    // Check if date is valid (handles Feb 30, etc.)
    if (
      date.getFullYear() === yearNum &&
      date.getMonth() === monthNum - 1 &&
      date.getDate() === dayNum
    ) {
      onChange(date);
    }
  }, [onChange, minYear, maxYear]);

  // Handle day input
  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTyping.current = true;
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDay(val);

    // Auto-jump to month when 2 digits entered
    if (val.length === 2) {
      setTimeout(() => {
        monthRef.current?.focus();
        isUserTyping.current = false;
      }, 10);
    } else {
      setTimeout(() => { isUserTyping.current = false; }, 100);
    }

    emitDate(val, month, year);
  };

  // Handle month input
  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTyping.current = true;
    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMonth(val);

    // Auto-jump to year when 2 digits entered
    if (val.length === 2) {
      setTimeout(() => {
        yearRef.current?.focus();
        isUserTyping.current = false;
      }, 10);
    } else {
      setTimeout(() => { isUserTyping.current = false; }, 100);
    }

    emitDate(day, val, year);
  };

  // Handle year input
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTyping.current = true;
    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(val);
    emitDate(day, month, val);
    setTimeout(() => { isUserTyping.current = false; }, 100);
  };

  // Get max days for a given month/year
  const getMaxDays = (m: number, y: number) => {
    if (!m || !y) return 31;
    return new Date(y, m, 0).getDate();
  };

  // Handle backspace and arrow keys
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'day' | 'month' | 'year'
  ) => {
    const target = e.target as HTMLInputElement;
    
    // Backspace navigation
    if (e.key === 'Backspace') {
      if (target.value === '' || target.selectionStart === 0) {
        if (field === 'month') {
          dayRef.current?.focus();
        } else if (field === 'year') {
          monthRef.current?.focus();
        }
      }
    }
    
    // Arrow left/right navigation between fields
    if (e.key === 'ArrowRight' && target.selectionStart === target.value.length) {
      if (field === 'day') monthRef.current?.focus();
      if (field === 'month') yearRef.current?.focus();
    }
    if (e.key === 'ArrowLeft' && target.selectionStart === 0) {
      if (field === 'month') dayRef.current?.focus();
      if (field === 'year') monthRef.current?.focus();
    }

    // Arrow up/down to increment/decrement values
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const delta = e.key === 'ArrowUp' ? 1 : -1;
      
      if (field === 'day') {
        const currentDay = parseInt(day, 10) || 0;
        const maxDay = getMaxDays(parseInt(month, 10), parseInt(year, 10) || new Date().getFullYear());
        let newDay = currentDay + delta;
        if (newDay < 1) newDay = maxDay;
        if (newDay > maxDay) newDay = 1;
        const newDayStr = String(newDay).padStart(2, '0');
        setDay(newDayStr);
        emitDate(newDayStr, month, year);
      }
      
      if (field === 'month') {
        const currentMonth = parseInt(month, 10) || 0;
        let newMonth = currentMonth + delta;
        if (newMonth < 1) newMonth = 12;
        if (newMonth > 12) newMonth = 1;
        const newMonthStr = String(newMonth).padStart(2, '0');
        setMonth(newMonthStr);
        // Adjust day if needed (e.g., 31 -> 30 for April)
        const maxDay = getMaxDays(newMonth, parseInt(year, 10) || new Date().getFullYear());
        if (parseInt(day, 10) > maxDay) {
          const newDayStr = String(maxDay).padStart(2, '0');
          setDay(newDayStr);
          emitDate(newDayStr, newMonthStr, year);
        } else {
          emitDate(day, newMonthStr, year);
        }
      }
      
      if (field === 'year') {
        const currentYear = parseInt(year, 10) || new Date().getFullYear();
        let newYear = currentYear + delta;
        if (newYear < minYear) newYear = maxYear;
        if (newYear > maxYear) newYear = minYear;
        const newYearStr = String(newYear);
        setYear(newYearStr);
        // Adjust day if needed (Feb 29 -> Feb 28 for non-leap years)
        const maxDay = getMaxDays(parseInt(month, 10), newYear);
        if (parseInt(day, 10) > maxDay) {
          const newDayStr = String(maxDay).padStart(2, '0');
          setDay(newDayStr);
          emitDate(newDayStr, month, newYearStr);
        } else {
          emitDate(day, month, newYearStr);
        }
      }
    }
  };

  const inputClass = cn(
    'w-full text-center font-mono text-base',
    'border-0 bg-transparent outline-none',
    'placeholder:text-gray-400',
    'focus:ring-0',
    disabled && 'opacity-50 cursor-not-allowed'
  );

  return (
    <div
      className={cn(
        'flex items-center gap-1 px-3 h-9 rounded-md border bg-transparent',
        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500',
        error && 'border-red-500 focus-within:ring-red-500',
        disabled && 'bg-gray-100',
        className
      )}
    >
      {/* Day */}
      <input
        ref={dayRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={day}
        onChange={handleDayChange}
        onKeyDown={(e) => handleKeyDown(e, 'day')}
        placeholder={placeholder.day}
        disabled={disabled}
        className={cn(inputClass, 'w-10')}
        maxLength={2}
        autoComplete="off"
      />
      
      <span className="text-gray-400 select-none">/</span>
      
      {/* Month */}
      <input
        ref={monthRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={month}
        onChange={handleMonthChange}
        onKeyDown={(e) => handleKeyDown(e, 'month')}
        placeholder={placeholder.month}
        disabled={disabled}
        className={cn(inputClass, 'w-10')}
        maxLength={2}
        autoComplete="off"
      />
      
      <span className="text-gray-400 select-none">/</span>
      
      {/* Year */}
      <input
        ref={yearRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={year}
        onChange={handleYearChange}
        onKeyDown={(e) => handleKeyDown(e, 'year')}
        placeholder={placeholder.year}
        disabled={disabled}
        className={cn(inputClass, 'w-14')}
        maxLength={4}
        autoComplete="off"
      />
    </div>
  );
}
